import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { usePartnerAccess } from "../../hooks/usePartnerAccess";
import { MyAELogo } from "./Logo";
import {
  LayoutDashboard, FilePlus2, FileText, FolderOpen, User, CreditCard,
  LifeBuoy, Shield, Menu, X, LogOut, LogIn, Users, Briefcase,
} from "lucide-react";

const NAV = [
  { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
  { label: "New Analysis", to: "/new", icon: FilePlus2 },
  { label: "Reports", to: "/reports", icon: FileText },
  { label: "Groups", to: "/groups", icon: Users },
  { label: "Documents", to: "/documents", icon: FolderOpen },
  { label: "Account", to: "/profile", icon: User },
  { label: "Subscription", to: "/pricing", icon: CreditCard },
  { label: "Help Centre", to: "/contact", icon: LifeBuoy },
];

const NavItems = ({ pathname, go, isAdmin, isPartner, partnerLabel }) => (
  <nav className="flex-1 px-3 space-y-1" data-testid="sidebar-nav">
    {NAV.map((item) => {
      const active = pathname === item.to || (item.to !== "/" && pathname.startsWith(item.to + "/"));
      const Icon = item.icon;
      return (
        <button
          key={item.to}
          onClick={() => go(item.to)}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
            active ? "bg-indigo-50 text-indigo-700" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
          }`}
        >
          <Icon size={18} className={active ? "text-indigo-600" : "text-zinc-400"} />
          {item.label}
        </button>
      );
    })}
    {isPartner && (
      <button
        onClick={() => go("/partner")}
        data-testid="nav-partner-portal"
        title={partnerLabel ? `Partner Portal — ${partnerLabel}` : "Partner Portal"}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
          pathname === "/partner" || pathname.startsWith("/partner/")
            ? "bg-indigo-50 text-indigo-700"
            : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
        }`}
      >
        <Briefcase size={18} className={(pathname === "/partner" || pathname.startsWith("/partner/")) ? "text-indigo-600" : "text-zinc-400"} />
        <span className="truncate">Partner Portal</span>
      </button>
    )}
    {isAdmin && (
      <button
        onClick={() => go("/admin")}
        data-testid="nav-admin"
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
          pathname === "/admin" ? "bg-indigo-50 text-indigo-700" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
        }`}
      >
        <Shield size={18} className={pathname === "/admin" ? "text-indigo-600" : "text-zinc-400"} /> Admin
      </button>
    )}
  </nav>
);

const UserSnippet = ({ user, onSignIn, onSignOut }) => (
  <div className="border-t border-zinc-200 p-3">
    {user ? (
      <div className="flex items-center gap-3 px-2 py-1.5">
        <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-medium text-sm shrink-0">
          {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-zinc-900 truncate">{user.name || "Student"}</p>
          <p className="text-xs text-zinc-500 truncate">{user.email}</p>
        </div>
        <button onClick={onSignOut} data-testid="sidebar-signout" title="Sign out" className="text-zinc-400 hover:text-zinc-700 shrink-0">
          <LogOut size={16} />
        </button>
      </div>
    ) : (
      <button onClick={onSignIn} data-testid="sidebar-signin"
        className="w-full flex items-center justify-center gap-2 rounded-lg bg-zinc-900 text-white py-2.5 text-sm font-medium hover:bg-zinc-800 transition-colors">
        <LogIn size={16} /> Sign in
      </button>
    )}
  </div>
);

export const MyAELayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, setAuthOpen } = useAuth();
  const { hasAccess: isPartner, partner } = usePartnerAccess();
  const [drawer, setDrawer] = useState(false);
  const isAdmin = user && user.role === "admin";
  const go = (to) => { setDrawer(false); navigate(to); };

  return (
    <div className="min-h-screen bg-zinc-50 flex" data-testid="myae-layout">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-white border-r border-zinc-200">
        <div className="h-16 flex items-center px-5 border-b border-zinc-200">
          <button onClick={() => go("/dashboard")}><MyAELogo size={30} /></button>
        </div>
        <div className="flex-1 flex flex-col py-4 overflow-y-auto">
          <NavItems pathname={location.pathname} go={go} isAdmin={isAdmin} isPartner={isPartner} partnerLabel={partner?.label} />
        </div>
        <UserSnippet user={user} onSignIn={() => setAuthOpen(true)} onSignOut={logout} />
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {drawer && (
          <>
            <motion.div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setDrawer(false)}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
            <motion.aside className="fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-zinc-200 flex flex-col md:hidden"
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }} transition={{ type: "tween", duration: 0.25 }}>
              <div className="h-16 flex items-center justify-between px-5 border-b border-zinc-200">
                <MyAELogo size={28} />
                <button onClick={() => setDrawer(false)} data-testid="drawer-close"><X size={20} className="text-zinc-500" /></button>
              </div>
              <div className="flex-1 flex flex-col py-4 overflow-y-auto">
                <NavItems pathname={location.pathname} go={go} isAdmin={isAdmin} isPartner={isPartner} partnerLabel={partner?.label} />
              </div>
              <UserSnippet user={user} onSignIn={() => setAuthOpen(true)} onSignOut={logout} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex-1 md:pl-64 min-w-0">
        <div className="md:hidden h-16 flex items-center gap-3 px-4 bg-white border-b border-zinc-200 sticky top-0 z-30">
          <button onClick={() => setDrawer(true)} data-testid="open-drawer"><Menu size={22} className="text-zinc-700" /></button>
          <MyAELogo size={26} />
        </div>
        <motion.main initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {children}
        </motion.main>
      </div>
    </div>
  );
};
